// src/pages/budgeting/fobudgeting/SegmentBudgetingList.jsx - INTEGRATED VERSION
import React, { useState, useEffect } from 'react';
import { Plus, Eye, Edit2, Trash2, Search, ChevronDown, ChevronUp, MapPin, Navigation, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import SegmentBudgetingForm from './SegmentBudgetingForm';
import foSegmentService from '../../../services/foSegmentService';
import foSegmentBOQService from '../../../services/foSegmentBOQService';
import foSegmentRAPService from '../../../services/foSegmentRAPService';

const SegmentBudgetingList = () => {
  const [view, setView] = useState('list');
  const [selectedSegment, setSelectedSegment] = useState(null);
  
  // Data state
  const [segments, setSegments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Track which segments have BOQ and RAP
  const [segmentStatus, setSegmentStatus] = useState({});
  // { segmentId: { hasBOQ: true, hasRAP: false } }
  
  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegion, setFilterRegion] = useState('ALL');
  const [filterProvince, setFilterProvince] = useState('ALL');

  // ============= LIFECYCLE =============
  
  useEffect(() => {
    loadSegmentsWithBOQ();
  }, []);

  // ============= DATA LOADING =============
  
  /**
   * Load segments yang sudah punya BOQ
   */
  const loadSegmentsWithBOQ = async () => {
    setIsLoading(true);
    try {
      console.log('📂 Loading segments with BOQ...');
      
      // Get all FO segments
      const allSegments = await foSegmentService.getAllFOSegments();
      console.log('✅ Loaded segments:', allSegments.length);
      
      // Check which segments have BOQ
      const statusChecks = await Promise.all(
        allSegments.map(async (segment) => {
          try {
            // Check if BOQ exists
            const hasBOQ = await foSegmentBOQService.checkBOQExists(segment.id);
            
            // Check if RAP exists (check all categories)
            let hasRAP = false;
            if (hasBOQ) {
              const raps = await foSegmentRAPService.getRAPsBySegmentId(segment.id);
              hasRAP = raps && raps.length > 0;
            }
            
            return {
              segmentId: segment.id,
              hasBOQ,
              hasRAP
            };
          } catch (error) {
            console.error(`Error checking status for segment ${segment.id}:`, error);
            return {
              segmentId: segment.id,
              hasBOQ: false,
              hasRAP: false
            };
          }
        })
      );
      
      // Build status map
      const statusMap = {};
      statusChecks.forEach(status => {
        statusMap[status.segmentId] = {
          hasBOQ: status.hasBOQ,
          hasRAP: status.hasRAP
        };
      });
      
      setSegmentStatus(statusMap);
      
      // Filter segments that have BOQ
      const segmentsWithBOQ = allSegments.filter(segment => 
        statusMap[segment.id]?.hasBOQ
      );
      
      setSegments(segmentsWithBOQ);
      
      console.log('✅ Segments with BOQ:', segmentsWithBOQ.length);
      console.log('✅ Status map:', statusMap);
      
      if (segmentsWithBOQ.length === 0) {
        toast.error('No segments with BOQ found. Please create BOQ first.');
      }
      
    } catch (error) {
      console.error('❌ Error loading segments:', error);
      toast.error('Failed to load segments');
    } finally {
      setIsLoading(false);
    }
  };

  // ============= FILTERING =============
  
  const filteredSegments = segments.filter(segment => {
    const matchesSearch = 
      segment.spanId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      segment.siteNameAB?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      segment.kota?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by province
    const matchesProvince = filterProvince === 'ALL' || segment.mainArea === filterProvince;
    
    return matchesSearch && matchesProvince;
  });

  // Get unique provinces for filter
  const uniqueProvinces = [...new Set(segments.map(s => s.mainArea))].filter(Boolean).sort();

  // ============= EVENT HANDLERS =============
  
  /**
   * Handle Create/Edit Budgeting
   */
  const handleOpenBudgeting = (segment) => {
    setSelectedSegment(segment);
    setView('budgeting-form');
  };

  /**
   * Handle back to list
   */
  const handleBackToList = () => {
    setView('list');
    setSelectedSegment(null);
    // Reload to refresh status
    loadSegmentsWithBOQ();
  };

  /**
   * Handle budgeting saved
   */
  const handleBudgetingSaved = (segmentId) => {
    // Update status
    setSegmentStatus(prev => ({
      ...prev,
      [segmentId]: {
        ...prev[segmentId],
        hasRAP: true
      }
    }));
    
    toast.success('Budgeting saved successfully!');
    setView('list');
    setSelectedSegment(null);
  };

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    loadSegmentsWithBOQ();
    toast.success('Data refreshed');
  };

  // ============= HELPER FUNCTIONS =============
  
  /**
   * Format length display
   */
  const formatLength = (meters) => {
    if (!meters) return '-';
    return `${meters.toLocaleString('id-ID')} m`;
  };

  /**
   * Format length in km
   */
  const formatLengthKm = (meters) => {
    if (!meters) return '-';
    return `${(meters / 1000).toFixed(2)} km`;
  };

  /**
   * Get segment route display
   */
  const getRouteDisplay = (segment) => {
    // Parse siteNameAB to get site A and site B
    const siteNameAB = segment.siteNameAB || '';
    const parts = siteNameAB.split('→');
    
    if (parts.length === 2) {
      return {
        siteA: parts[0].trim(),
        siteB: parts[1].trim()
      };
    }
    
    // Fallback
    return {
      siteA: segment.siteNameAB || 'Site A',
      siteB: 'Site B'
    };
  };

  // ============= RENDER BUDGETING FORM =============
  
  if (view === 'budgeting-form' && selectedSegment) {
    return (
      <SegmentBudgetingForm 
        segment={selectedSegment} 
        onBack={handleBackToList} 
        onSave={handleBudgetingSaved} 
      />
    );
  }

  // ============= RENDER LIST VIEW =============
  
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">FO Segment Budgeting List</h1>
            <p className="text-gray-600 mt-1">Manage Fiber Optic segments Budgeting (RAP)</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        {/* Search and Filter Bar */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by Span ID, site name, or location..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              value={filterProvince}
              onChange={(e) => setFilterProvince(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent min-w-[200px]"
            >
              <option value="ALL">All Provinces</option>
              {uniqueProvinces.map(province => (
                <option key={province} value={province}>{province}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-600">Loading segments...</p>
            </div>
          </div>
        ) : filteredSegments.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg font-medium">No segments found</p>
              <p className="text-gray-500 text-sm mt-2">
                {searchTerm || filterProvince !== 'ALL' 
                  ? 'Try adjusting your search or filters' 
                  : 'No segments with BOQ available. Please create BOQ first.'}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">No</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Span ID</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Route</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Length</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSegments.map((segment, index) => {
                    const route = getRouteDisplay(segment);
                    const status = segmentStatus[segment.id] || {};
                    
                    return (
                      <tr key={segment.id} className="hover:bg-gray-50 transition-colors">
                        {/* No */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {index + 1}
                        </td>
                        
                        {/* Span ID */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">
                          {segment.spanId}
                        </td>
                        
                        {/* Route */}
                        <td className="px-6 py-4 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900 truncate max-w-xs">
                                {route.siteA}
                              </div>
                            </div>
                            <Navigation className="w-4 h-4 text-red-600 rotate-90 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900 truncate max-w-xs">
                                {route.siteB}
                              </div>
                            </div>
                          </div>
                        </td>
                        
                        {/* Location */}
                        <td className="px-6 py-4 text-sm">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div>
                              <div className="text-gray-900">{segment.kota || '-'}</div>
                              <div className="text-xs text-gray-500">{segment.mainArea || segment.main_area || '-'}</div>
                            </div>
                          </div>
                        </td>
                        
                        {/* Type */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                            {segment.ringType || 'FO'}
                          </span>
                        </td>
                        
                        {/* Length */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="font-semibold text-gray-900">
                            {formatLength(segment.commercialDistance || segment.lengthPlan)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatLengthKm(segment.commercialDistance || segment.lengthPlan)}
                          </div>
                        </td>
                        
                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex flex-col gap-1">
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                              BOQ ✓
                            </span>
                            {status.hasRAP && (
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">
                                RAP ✓
                              </span>
                            )}
                          </div>
                        </td>
                        
                        {/* Actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex justify-center">
                            <button
                              onClick={() => handleOpenBudgeting(segment)}
                              className={`font-medium px-4 py-2 rounded-lg transition-all shadow-md hover:shadow-lg ${
                                status.hasRAP
                                  ? 'bg-green-600 text-white hover:bg-green-700'
                                  : 'bg-red-600 text-white hover:bg-red-700'
                              }`}
                            >
                              {status.hasRAP ? 'Edit Budgeting' : 'Create Budgeting'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{filteredSegments.length}</span> of{' '}
                  <span className="font-medium">{segments.length}</span> segments with BOQ
                </div>
                <div className="flex gap-6 text-sm">
                  <div className="text-gray-700">
                    Total Length:{' '}
                    <span className="font-semibold text-blue-600">
                      {formatLength(
                        filteredSegments.reduce((sum, s) => 
                          sum + (s.commercialDistance || s.lengthPlan || 0), 0
                        )
                      )}
                    </span>
                  </div>
                  <div className="text-gray-700">
                    With RAP:{' '}
                    <span className="font-semibold text-green-600">
                      {filteredSegments.filter(s => segmentStatus[s.id]?.hasRAP).length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SegmentBudgetingList;